/**
 * A regra do ditado, sem DOM e sem rede.
 *
 * O que chega do servidor são segmentos avulsos, cada um com o seu `seq`, e
 * eles chegam fora de ordem e às vezes repetidos — o replay do `assinar`
 * reentrega o que já veio pelo socket. Juntar isso é a única decisão do
 * ditado que vale um teste, então ela mora aqui.
 */

export interface SegmentoDeDitado {
  seq: number;
  texto: string;
}

/**
 * Guarda um segmento novo, sem duplicar e sem desordenar.
 *
 * Devolve a mesma lista quando o `seq` já existe: assim quem chama pode
 * comparar por identidade e não repintar a tela à toa.
 */
export function guardarSegmento(
  atuais: readonly SegmentoDeDitado[],
  novo: SegmentoDeDitado,
): SegmentoDeDitado[] {
  if (atuais.some((s) => s.seq === novo.seq)) return atuais as SegmentoDeDitado[];
  return [...atuais, novo].sort((a, b) => a.seq - b.seq);
}

/**
 * O texto ditado, na ordem em que foi falado.
 *
 * Espaço simples entre segmentos e nas pontas aparado: o provedor devolve cada
 * trecho já pontuado, mas com folga de espaço nas bordas, e emendar sem
 * cuidado produz "hoje .Amanhã".
 */
export function juntarSegmentos(segmentos: readonly SegmentoDeDitado[]): string {
  return segmentos
    .map((s) => s.texto.trim())
    .filter((t) => t.length > 0)
    .join(' ');
}

/**
 * Enfia o ditado no texto que já estava escrito, na posição do cursor.
 *
 * Não é concatenação: quem dita no meio de uma frase espera que caia ali. O
 * espaço só entra quando falta — ditar depois de "para o " não pode virar
 * "para o  cliente".
 */
export function inserirNoCursor(texto: string, ditado: string, cursor: number): string {
  if (!ditado) return texto;
  const onde = Math.max(0, Math.min(cursor, texto.length));
  const antes = texto.slice(0, onde);
  const depois = texto.slice(onde);
  const abertura = antes.length > 0 && !/\s$/.test(antes) ? ' ' : '';
  const fechamento = depois.length > 0 && !/^\s/.test(depois) ? ' ' : '';
  return antes + abertura + ditado + fechamento + depois;
}
